// Señales de INTENSIDAD de uso del operador (Item B2-emisión) + contador de core-actions
// per-usuario que consume el feedback-por-uso del preview (Item E#3).
//
// Dos responsabilidades, una sola superficie para no duplicar el concepto de "acción core":
//   1. B2 — emitir a GA4 (reusando el sink de analytics) cada acción de edición de menú con
//      `core_action=true` + `by_user_id`, y un heartbeat de minutos activos. Permite al dashboard
//      identificar al "heavy daily operator" y ponderar después el feedback por intensidad.
//   2. E#3 — llevar un contador per-usuario (per-tab, en memoria) de core-actions para disparar
//      el prompt de feedback OMITIBLE tras N acciones reales. NO se persiste como variante: es
//      una señal de uso, no estado de rollout.
//
// SIN PII: solo `by_user_id` (el id de usuario que el resto de telemetría ya emite) + el tipo de
// acción. Nunca nombre, email ni contenido del producto. Si el sink está apagado (no-prod / sin
// tracking), `ga4Event` es no-op y nada rompe.

import { ga4Event } from '@utils/analytics/ga4';

// Tipos de acción core del menú. Estables (viajan en el payload GA4, no se traducen).
export type CoreActionType = 'product_create' | 'product_edit' | 'product_reorder';

// Nombre de evento GA4 (snake_case, coherente con el resto del catálogo de rollout).
const GA4_CORE_ACTION_EVENT = 'rollout_core_action';
const GA4_HEARTBEAT_EVENT = 'rollout_active_heartbeat';

// Cadencia del heartbeat de minutos activos: una marca por minuto de actividad real (no por
// tiempo de pestaña abierta inactiva). El consumidor llama `pingActivity()` ante interacción;
// solo emitimos si pasó al menos un minuto desde el último latido.
const HEARTBEAT_INTERVAL_MS = 60_000;

function byUserId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem('id_user') ?? '';
  } catch {
    return '';
  }
}

// ── Contador de core-actions per-usuario (per-tab, en memoria) ─────────────────────────
// Keyed por user_id para que un cambio de usuario en la misma pestaña (multisucursal) no
// herede el conteo del anterior. No se persiste: el prompt de feedback es per-sesión-de-uso.
const coreActionCounts = new Map<string, number>();
type CountListener = (count: number) => void;
const countListeners = new Set<CountListener>();

function notifyCount(count: number): void {
  countListeners.forEach((l) => l(count));
}

// Suscribe al contador de core-actions del usuario activo. Devuelve unsubscribe.
export function subscribeCoreActionCount(listener: CountListener): () => void {
  countListeners.add(listener);
  return () => {
    countListeners.delete(listener);
  };
}

export function getCoreActionCount(): number {
  return coreActionCounts.get(byUserId()) ?? 0;
}

// ── Heartbeat de minutos activos ───────────────────────────────────────────────────────
let lastHeartbeatAt = 0;

// Marca actividad. Emite un latido a GA4 a lo sumo una vez por minuto. Idempotente dentro de
// la ventana: llamarlo en ráfaga no infla el conteo de minutos activos.
export function pingActivity(): void {
  const now = Date.now();
  if (now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;
  lastHeartbeatAt = now;
  ga4Event(GA4_HEARTBEAT_EVENT, { by_user_id: byUserId() });
}

// ── Registro de una core-action ─────────────────────────────────────────────────────────
// Emite la señal B2 a GA4 (core_action=true + by_user_id, sin PII), cuenta también como
// actividad (heartbeat) e incrementa el contador per-usuario que consume E#3. Fire-and-forget.
export function recordMenuCoreAction(action: CoreActionType): void {
  const userId = byUserId();

  // B2 — señal a GA4. El `rollout_variant` ya viaja como user_property global (applyFeatureVersionTags),
  // así el dashboard puede slicear por variante sin que lo repitamos aquí.
  ga4Event(GA4_CORE_ACTION_EVENT, { core_action: true, action, by_user_id: userId });

  // Toda core-action cuenta como actividad para el heartbeat de minutos activos.
  pingActivity();

  // E#3 — contador per-usuario (per-tab) que dispara el prompt de feedback por uso.
  const next = (coreActionCounts.get(userId) ?? 0) + 1;
  coreActionCounts.set(userId, next);
  notifyCount(next);
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetUsageSignalsForTests(): void {
  coreActionCounts.clear();
  countListeners.clear();
  lastHeartbeatAt = 0;
}
